import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Sparkles } from 'lucide-react';

const PricingCard = ({
  plan,
  interval = 'monthly',
  onSelect,
  currentPlan,
  loading,
  isAuthenticated
}) => {
  const isCurrentPlan = currentPlan === plan.id;
  const isPopular = plan.popular;
  const isFree = plan.priceMonthly === 0;
  const isEnterprise = plan.priceMonthly === null;

  const getPrice = () => {
    if (isFree) return 'Free';
    if (isEnterprise) return 'Custom';
    return interval === 'yearly'
      ? `$${Math.round(plan.priceYearly / 12)}`
      : `$${plan.priceMonthly}`;
  };

  const getButtonText = () => {
    if (loading) return 'Loading...';
    if (isCurrentPlan) return 'Current plan';
    if (isFree) return 'Get started';
    if (isEnterprise) return 'Contact sales';
    return `Upgrade to ${plan.name}`;
  };

  const getButtonStyle = () => {
    if (isCurrentPlan) {
      return 'border border-[rgba(32,30,29,0.18)] bg-sand-100 text-sand-600 cursor-default';
    }
    if (isPopular) {
      return 'bg-bill-500 text-white hover:bg-bill-600 shadow-brand-sm';
    }
    return 'border border-[rgba(32,30,29,0.18)] bg-white text-ink hover:bg-sand-100';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ scale: 1.02, y: -4 }}
      className={`relative rounded-3xl p-6 transition-all duration-300 ${
        isPopular
          ? 'border-2 border-bill-500 bg-bill-100 shadow-brand-md'
          : 'border border-[rgba(32,30,29,0.12)] bg-white hover:bg-sand-100 hover:border-[rgba(32,30,29,0.2)] shadow-brand-sm'
      }`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <motion.span
            className="inline-flex items-center gap-1.5 rounded-full bg-bill-500 px-4 py-1.5 text-xs font-semibold text-white shadow-brand-sm"
            animate={{ y: [0, -2, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Sparkles className="h-3 w-3" />
            Most popular
          </motion.span>
        </div>
      )}

      <div className="mb-5">
        <h3 className="text-xl font-semibold text-ink">{plan.name}</h3>
        <p className="mt-1 text-sm text-sand-600">{plan.label}</p>
      </div>

      <div className="mb-5">
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-bold text-ink">{getPrice()}</span>
          {!isFree && !isEnterprise && (
            <span className="text-sm text-sand-600">/month</span>
          )}
        </div>
        {interval === 'yearly' && plan.savings > 0 && (
          <p className="mt-2 text-sm text-sage-600 font-medium">
            Save ${plan.savings}/year
          </p>
        )}
        {!isFree && !isEnterprise && interval === 'yearly' && (
          <p className="mt-1 text-xs text-sand-600">
            Billed ${plan.priceYearly}/year
          </p>
        )}
      </div>

      <p className="mb-6 text-sm text-sand-700 leading-relaxed">{plan.description}</p>

      <motion.button
        type="button"
        onClick={() => !isCurrentPlan && onSelect && onSelect(plan, interval)}
        disabled={isCurrentPlan || loading}
        className={`mb-6 w-full rounded-full px-4 py-3 text-sm font-semibold transition-all disabled:opacity-50 ${getButtonStyle()}`}
        whileHover={!isCurrentPlan ? { scale: 1.02 } : {}}
        whileTap={!isCurrentPlan ? { scale: 0.98 } : {}}
      >
        {getButtonText()}
      </motion.button>

      <ul className="space-y-3">
        {plan.perks.map((perk, index) => (
          <li
            key={index}
            className={`flex items-start gap-2.5 text-sm ${
              index === 0 && perk.startsWith('Everything')
                ? 'text-sand-600 font-medium'
                : 'text-sand-700'
            }`}
          >
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-sage-600" />
            <span>{perk}</span>
          </li>
        ))}
      </ul>

      {plan.limitations && plan.limitations.length > 0 && (
        <div className="mt-5 pt-5 border-t border-[rgba(32,30,29,0.12)]">
          <p className="text-xs text-sand-600 mb-2 font-medium">Limitations:</p>
          <ul className="space-y-1.5">
            {plan.limitations.map((limitation, index) => (
              <li key={index} className="text-xs text-sand-600">
                {limitation}
              </li>
            ))}
          </ul>
        </div>
      )}
    </motion.div>
  );
};

export default PricingCard;
